import { perspectiveInterface } from 'modelInterface/perspectives/perspectiveInterface';

import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  notifyPerspectivesDestroyed,
  setActivePerspective,
} from 'streams/filters/FilterActions';
import { action$, ofType } from '@ardoq/rxbeach';
import { combineLatest, from } from 'rxjs';
import { context$ } from 'streams/context/context$';
import {
  editorOpened,
  NotifySavedPerspectivesOptionsChangedPayload,
} from 'perspective/actions';
import { getFormattedSavedPerspectives } from './utils';
import { GroupingRule } from '@ardoq/api-types';
import {
  SelectedSavedPerspectiveOptions,
  StoredPerspectivesOption,
} from '@ardoq/perspectives';
import { workspaceInterface } from '@ardoq/workspace-interface';
import {
  getTagIdsAndWorkspaceIdsOfClosedWorkspacesUsedInGroupingRules,
  getVirtualReferenceTypesByWorkspaceFromLoadedGraph,
  parseAvailiableTags,
  toGroupingOptionsIncludingSavedPerspectiveEntriesFromClosedWorkspaces,
} from './perspectiveEditorGroupingOptions$';
import type { VirtualReferenceTypesByWorkspace } from './groupingOptionsPerWorkspace';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import {
  scopeDataOperations,
  type WorkspaceWithTypes,
} from '@ardoq/scope-data';
import { getFormattingOptions } from './perspectiveEditorFormattingOptions$';
import { mapSavedPerspectiveToPerspectiveEditorSavedPerspectiveState } from './mapSavedPerspectiveToPerspectiveEditorSavedPerspectiveState';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import { fields$, type FieldsState } from 'streams/fields/fields$';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import { mapToPerspectiveGroupingRule } from './mapToPerspectiveGroupingRule';
import { uniq } from 'lodash';
import { isTruthy } from '@ardoq/common-helpers';
import { handleError, tagApi } from '@ardoq/api';
import { ContextShape, WorkspaceAndModelLookup } from '@ardoq/data-model';
import { APITagAttributes } from '@ardoq/api-types';

const savedPerspectiveEditorOptions$ = action$.pipe(
  ofType(editorOpened, setActivePerspective, notifyPerspectivesDestroyed),
  map((): StoredPerspectivesOption[] => {
    const savedPerspectives = perspectiveInterface.getAll();
    const savedPerspectivesPermissions =
      perspectiveInterface.getSavedPerspectivesPermissions();
    return getFormattedSavedPerspectives({
      savedPerspectives,
      savedPerspectivesPermissions,
    });
  })
);

export const savedPerspectiveOptions$ = combineLatest([
  savedPerspectiveEditorOptions$,
  context$,
]).pipe(
  map(
    ([
      savedPerspectiveOptions,
      { workspaceId },
    ]): NotifySavedPerspectivesOptionsChangedPayload => ({
      savedPerspectives: savedPerspectiveOptions,
      isOnlyReaderRole: !workspaceInterface.hasWriteAccess(workspaceId),
    })
  )
);

/**
 * We can load a saved perspective that has some applied changes.
 * (e.g. user loads a saved perspective, then applies some changes
 * and closes Perspective Editor, then he/she comes back to save his/her changes).
 *
 * In that case, we need both:
 * - current formatting, filtering and grouping rules (provided by formatting and grouping options)
 * - formatting, filtering and grouping rules saved in saved perspective (savedPerspective)
 *
 * in order to:
 * - display currently applied rules (saved perspective + changes),
 * - run change detection between saved perspective and current state of Perspective editor.
 *
 */
export const getCurrentlyLoadedSavedPerspectiveAndLoadMissingData = (
  openWorkspacesWithTypes: WorkspaceWithTypes[],
  allWorkspacesWithTypes: WorkspaceWithTypes[],
  fetchedTags: Array<{ id: string; label: string; workspaceId: string }>,
  fields: FieldsState,
  virtualReferenceTypesByWorkspace: VirtualReferenceTypesByWorkspace
): SelectedSavedPerspectiveOptions | null => {
  const savedPerspectiveId =
    perspectiveInterface.getSelectedSavedPerspectiveId();
  const savedPerspective = perspectiveInterface.getById(savedPerspectiveId);

  if (!savedPerspective) {
    return null;
  }

  const workspaceIds = openWorkspacesWithTypes.map(
    ({ workspace }) => workspace._id
  );
  const formattingOptions = getFormattingOptions(workspaceIds, fields);
  const groupsOptions =
    toGroupingOptionsIncludingSavedPerspectiveEntriesFromClosedWorkspaces(
      openWorkspacesWithTypes,
      allWorkspacesWithTypes,
      fetchedTags,
      virtualReferenceTypesByWorkspace
    );

  return {
    savedPerspective:
      mapSavedPerspectiveToPerspectiveEditorSavedPerspectiveState(
        savedPerspective
      ),
    groupsOptions,
    formattingOptions,
  };
};

const toSavedPerspectiveValues = ({
  context,
  workspaceAndModel,
  tags,
  fields,
  virtualReferenceTypesByWorkspace,
}: {
  context: ContextShape;
  workspaceAndModel: WorkspaceAndModelLookup;
  tags: APITagAttributes[];
  fields: FieldsState;
  virtualReferenceTypesByWorkspace: VirtualReferenceTypesByWorkspace;
}): SelectedSavedPerspectiveOptions | null => {
  const openWorkspacesWithTypes = scopeDataOperations.toWorkspacesWithTypes(
    workspaceAndModel,
    context.workspaceIds
  );

  const appliedGroupingRules: GroupingRule[] = groupingRuleInterface
    .getAll()
    .map(mapToPerspectiveGroupingRule);

  const closedWorkspaceIdsUsedInGroupingRules = uniq(
    appliedGroupingRules
      .map(groupingRule => groupingRule.workspaceId)
      .filter(isTruthy)
      .filter(workspaceId => !context.workspaceIds.includes(workspaceId))
  );

  const allWorkspaceIds = [
    ...context.workspaceIds,
    ...closedWorkspaceIdsUsedInGroupingRules,
  ];

  const allWorkspacesWithTypes = scopeDataOperations.toWorkspacesWithTypes(
    workspaceAndModel,
    allWorkspaceIds
  );

  const fetchedTags = parseAvailiableTags(tags);

  return getCurrentlyLoadedSavedPerspectiveAndLoadMissingData(
    openWorkspacesWithTypes,
    allWorkspacesWithTypes,
    fetchedTags,
    fields,
    virtualReferenceTypesByWorkspace
  );
};
export const currentlySelectedSavedPerspectiveValues$ = action$.pipe(
  ofType(setActivePerspective, editorOpened),
  withLatestFrom(context$, workspaceAndModel$, fields$, loadedGraph$),
  map(([, context, workspaceAndModel, fields, loadedGraph]) => {
    const appliedGroupingRules: GroupingRule[] = groupingRuleInterface
      .getAll()
      .map(mapToPerspectiveGroupingRule);

    const tagIds =
      getTagIdsAndWorkspaceIdsOfClosedWorkspacesUsedInGroupingRules(
        appliedGroupingRules,
        context.workspaceIds
      ).map(({ id }) => id);

    return {
      context,
      workspaceAndModel,
      fields,
      tagIds,
      virtualReferenceTypesByWorkspace:
        getVirtualReferenceTypesByWorkspaceFromLoadedGraph(loadedGraph),
    };
  }),
  switchMap(({ tagIds, ...rest }) =>
    from(tagApi.fetchTags(tagIds)).pipe(
      handleError(() => []),
      map(tags => ({ ...rest, tags }))
    )
  ),
  map(toSavedPerspectiveValues)
);
